/**
 * Incident-card helpers — pure, React-free utilities for working with the A2UI
 * blocks an assistant turn emits. They fuse an `alert_card` with the
 * `confirm_action` that immediately follows it (so the incident reads as one
 * card with an inline confirm), and derive a proactive-banner descriptor from a
 * pending inbox item.
 */
import type { A2uiPayload } from './types';

/** Severity union an alert_card / proactive banner can carry. */
export type ProactiveSeverity = 'info' | 'warning' | 'critical';

/** Proactive-banner descriptor derived from the first pending inbox item. */
export interface Proactive {
  active: boolean;
  severity: ProactiveSeverity;
  message: string;
  action: {
    action_id: string;
    title: string;
    confirm_label?: string;
  } | null;
}

/** A fused alert_card payload: a shallow copy of the alert_card plus the
 *  adjacent confirm_action block under `_fusedConfirm`. */
export interface FusedAlertCard extends A2uiPayload {
  _fusedConfirm?: A2uiPayload;
}

const str = (v: unknown): string => (v == null ? '' : String(v));

const chartScope = (b: A2uiPayload): string =>
  `timeseries_chart:${str(b.title)
    .replace(/\s*\([^)]*\b\d{4}\b[^)]*\)\s*$/, '')
    .trim()}:${str(b.unit)}`;

const chartHasPoints = (b: A2uiPayload): boolean =>
  Array.isArray(b.series) &&
  b.series.some(
    (series) =>
      !!series &&
      typeof series === 'object' &&
      Array.isArray((series as { points?: unknown }).points) &&
      (series as { points: unknown[] }).points.length > 0
  );

/**
 * Stable identity for a UI block, or `null` if the block must never be collapsed
 * (e.g. free-form markdown, confirm cards, or device-less alert cards that
 * legitimately repeat within a turn). Blocks that represent a single, re-emittable view —
 * a table, a device/key-value/map/chart/digest card — get a key derived from
 * their defining fields so a later emission supersedes an earlier draft.
 */
const blockIdentity = (b: A2uiPayload): string | null => {
  switch (b.component) {
    case 'fleet_table':
      // Include the column signature so genuinely different tables stay distinct,
      // while an empty/partial draft and its populated final (same columns)
      // collapse into one.
      return `fleet_table:${str(b.title)}:${(Array.isArray(b.columns)
        ? b.columns
        : []
      ).join('|')}`;
    case 'device_card':
      return `device_card:${str(b.device_id)}`;
    case 'timeseries_chart':
      return `timeseries_chart:${str(b.title)}:${str(b.unit)}`;
    case 'sensor_history':
      return `sensor_history:${str(b.device_id)}:${str(b.start)}:${str(b.end)}`;
    case 'map_card':
      return `map_card:${str(b.title)}`;
    case 'key_value':
      return `key_value:${str(b.title)}`;
    case 'digest_card':
      return `digest_card:${str(b.date)}`;
    case 'report_card':
      // A re-emitted report_card for the same saved definition supersedes the
      // earlier (possibly draft) block.
      return `report_card:${str(b.report_def_id)}`;
    case 'email_preview':
      // An email draft is keyed by its action_id so a revised draft replaces the
      // earlier one instead of stacking a second compose card.
      return `email_preview:${str(b.action_id)}`;
    case 'alert_card':
      // The same incident re-emitted (reworded) within a turn supersedes the
      // earlier card; distinct incidents differ in device/severity/rule. Cards
      // without a device_id stay uncollapsible.
      return b.device_id
        ? `alert_card:${str(b.device_id)}:${str(b.severity)}:${str(b.rule)}`
        : null;
    default:
      // markdown, confirm_action and unknowns can legitimately
      // recur within a turn — never collapse them.
      return null;
  }
};

/**
 * Collapse repeated emissions of the same logical view, keeping the **last**
 * (most complete) instance at its final position. The agent sometimes emits a
 * view more than once in a turn — a partial/empty draft while it is still
 * fetching, then the populated final — and the provider appends every emitted
 * block, so without this the same card renders 2–3 times. Blocks with no stable
 * identity pass through untouched, preserving order.
 */
export const dedupeUiBlocks = (blocks: A2uiPayload[]): A2uiPayload[] => {
  const seen = new Set<string>();
  const populatedChartScopes = new Set<string>();
  const keptReversed: A2uiPayload[] = [];
  for (let i = blocks.length - 1; i >= 0; i -= 1) {
    const block = blocks[i];
    if (block.component === 'timeseries_chart') {
      const scope = chartScope(block);
      const hasPoints = chartHasPoints(block);
      if (!hasPoints && populatedChartScopes.has(scope)) continue;
      if (hasPoints) populatedChartScopes.add(scope);
    }
    const key = blockIdentity(block);
    if (key !== null) {
      if (seen.has(key)) continue;
      seen.add(key);
    }
    keptReversed.push(block);
  }
  return keptReversed.reverse();
};

/** First `alert_card` block, if any. */
export const alertCardIn = (blocks: A2uiPayload[]): A2uiPayload | undefined =>
  blocks.find((b) => b.component === 'alert_card');

/** First `confirm_action` block, if any. */
export const confirmActionIn = (
  blocks: A2uiPayload[]
): A2uiPayload | undefined =>
  blocks.find((b) => b.component === 'confirm_action');

/**
 * Fuse adjacent alert_card + confirm_action pairs that target the same device.
 *
 * Whenever block[i] is an `alert_card` and block[i+1] is a `confirm_action`,
 * and both carry a truthy, equal `device_id`, emit a single block — a shallow
 * copy of block[i] with the confirm_action attached under `_fusedConfirm` — and
 * skip block[i+1]. All other blocks pass through unchanged.
 */
export const fuseIncidentCards = (blocks: A2uiPayload[]): A2uiPayload[] => {
  const out: A2uiPayload[] = [];
  for (let i = 0; i < blocks.length; i += 1) {
    const cur = blocks[i];
    const next = blocks[i + 1];
    if (
      cur?.component === 'alert_card' &&
      next?.component === 'confirm_action' &&
      cur.device_id &&
      next.device_id &&
      cur.device_id === next.device_id
    ) {
      const fused: FusedAlertCard = { ...cur, _fusedConfirm: next };
      out.push(fused);
      i += 1; // skip the consumed confirm_action
      continue;
    }
    out.push(cur);
  }
  return out;
};

/**
 * Derive a proactive-banner descriptor from a pending inbox item (or undefined).
 * Severity comes from the alert_card in the item's ui_blocks (default 'warning');
 * the action comes from a confirm_action block, if present.
 */
export const deriveProactive = (
  item: { text: string; ui_blocks: A2uiPayload[] | null } | undefined
): Proactive | null => {
  if (!item) return null;
  const blocks = item.ui_blocks ?? [];
  const alert = alertCardIn(blocks);
  const confirm = confirmActionIn(blocks);
  const severity = (alert?.severity as ProactiveSeverity) ?? 'warning';
  return {
    active: true,
    message: item.text,
    severity,
    action: confirm
      ? {
          action_id: confirm.action_id as string,
          title: confirm.title as string,
          confirm_label: confirm.confirm_label as string | undefined,
        }
      : null,
  };
};
